import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Auth } from "aws-amplify";
import { FiLogOut } from "react-icons/fi";

export default function CommonSidebar({
  collapsed,
  toggleSidebar,
  isMobile,
  menuItems,
  role = "admin",
  pathPrefix = ""
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const [activeSubmenu, setActiveSubmenu] = useState(null);

  // Auto-open the submenu whose route we're currently on, and close any others.
  useEffect(() => {
    const matchedIdx = menuItems?.findIndex(
      (item) =>
        item.submenu &&
        item.submenu.some((sub) => currentPath.startsWith(sub.path))
    );
    setActiveSubmenu(matchedIdx >= 0 ? matchedIdx : null);
  }, [currentPath, menuItems]);

  // Check if path is active - only exact matches count
  const isActive = (path) => {
    return path === currentPath;
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate("/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) toggleSidebar();
  };

  const handleMenuItemClick = (item, index) => {
    if (item.submenu && item.submenu.length > 0) {
      const isAlreadyOpen = activeSubmenu === index;
      const isInsideSubmenu = item.submenu.some((sub) =>
        currentPath.startsWith(sub.path)
      );

      // If it's already open and we're on one of its sub-routes, just collapse it.
      // Otherwise, expand it and land the user on the first sub-item so both the
      // parent and the first submenu entry highlight correctly.
      if (isAlreadyOpen && isInsideSubmenu) {
        setActiveSubmenu(null);
        return;
      }

      setActiveSubmenu(index);
      const targetPath = item.submenu[0].path;
      if (targetPath !== currentPath) {
        handleNavigate(targetPath);
      } else if (isMobile) {
        toggleSidebar();
      }
    } else {
      handleNavigate(item.path);
    }
  };

  const sidebarClass = `
    sidebar d-flex flex-column px-2 border-end 
    ${collapsed ? (isMobile ? "" : "collapsed") : isMobile ? "mobile-open" : ""}
  `;

  return (
    <div
      className={sidebarClass}
      style={!isMobile && !collapsed ? { minWidth: 260 } : {}}
    >
      {/* Header */}
      <div
        className={`sidebar-header d-flex align-items-center justify-content-between p-3 ${
          collapsed ? "flex-column gap-2" : ""
        }`}
      >
        {collapsed ? (
          <img src="/images/Logo.png" alt="Logo" height="32" />
        ) : (
          <img src="/images/Logo.svg" alt="Logo" height="36" />
        )}
        <button
          className="btn btn-sm p-0 border-0 bg-transparent"
          onClick={toggleSidebar}
        >
          {isMobile ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <img
              src={collapsed ? "/images/Toggle2.svg" : "/images/Toggle.svg"}
              alt="Menu"
              width="24"
              height="24"
            />
          )}
        </button>
      </div>

      {!collapsed && (
        <div className="px-3 mb-2 text-muted small menu-label">Menu</div>
      )}

      {/* Menu Items */}
      <ul className="nav flex-column sidebar-menu">
        {menuItems?.map((item, index) => (
          <React.Fragment key={index}>
            <li
              className={`nav-item ${
                item.submenu
                  ? activeSubmenu === index
                    ? "active"
                    : ""
                  : isActive(item.path)
                  ? "active"
                  : ""
              }`}
              onClick={() => handleMenuItemClick(item, index)}
              style={{ cursor: "pointer" }}
            >
              <img src={`/images/${item.icon}`} alt={item.label} />
              {!collapsed && (
                <span
                  className="d-flex justify-content-between w-100"
                  style={{ alignItems: "center" }}
                >
                  {item.label}
                  {item.submenu && (
                    <img
                      src={`/images/${
                        activeSubmenu === index ? "minus" : "plus"
                      }.svg`}
                      alt="toggle"
                    />
                  )}
                </span>
              )}
            </li>

            {/* Submenu Items */}
            {!collapsed && item.submenu && activeSubmenu === index && (
              <div className="submenu ps-4">
                {item.submenu.map((subItem, subIdx) => (
                  <div
                    key={subIdx}
                    className={`submenu-item mb-1 ${
                      isActive(subItem.path) ? "active" : ""
                    }`}
                    onClick={() => handleNavigate(subItem.path)}
                    style={{ cursor: "pointer" }}
                  >
                    <span>{subItem.label}</span>
                  </div>
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
      </ul>

      {/* Logout Button */}
      <div className="mt-auto mb-3 px-3">
        <button
          onClick={handleLogout}
          className="btn btn-danger w-100 d-flex align-items-center"
          style={{
            cursor: "pointer",
            padding: collapsed ? "2px 5px" : "8px 16px",
            justifyContent: collapsed ? "center" : "flex-start",
            overflow: "visible",
          }}
        >
          <FiLogOut size={20} />
          {!collapsed && <span style={{ marginLeft: "8px" }}>Logout</span>}
        </button>
      </div>
    </div>
  );
}
